import { ComponentBase } from 'utils/abstract-classes/component-base'
import { CLASS_NAME } from 'constants/class-names'
const { inventoryListing: className } = CLASS_NAME
import { getClutchGeoContext } from './utils'
import type { GeoIPContext } from './utils'
import { CarouselComponent } from 'components/main/carousel/carousel.component'

type ClutchResponse = {
    page: number
    pageSize: number
    totalCount: number
    vehicles: ClutchVehicle[]
    isRecommendation: boolean
    ctaURL: string
}

type ClutchVehicle = {
    cardPhotoUrl: string
    name: string
    id: number
    mileage: number
    type: string
    year: number
    make: NameItem
    model: NameItem
    trim: NameItem
    drivetrain: NameItem
    vehiclePrices: VehiclePrice[]
    websiteState: null | string
}

type NameItem = {
    id: number
    name: string
}
type VehiclePrice = {
    id: string
    price: number
    adminFee: number
    marketingPriceChange: MarketingPriceChange
    createdAt: string
    updatedAt: string
    provinceId: string
    vehicleId: number
    previousPrice: number
    promoPrice: null | number
}

type MarketingPriceChange = {
    oldPrice: number
    willSendTrigger: boolean
}

const VEHICLES_MAX_COUNT = 15

export class InventoryListingComponent extends ComponentBase {
    private wrapper: HTMLDivElement
    private geoContext: GeoIPContext | undefined

    public constructor(wrapper: HTMLDivElement) {
        super()

        this.wrapper = wrapper
        void this.render()
    }

    private async render() {
        this.geoContext = await getClutchGeoContext()
        if (!this.geoContext) {
            this.removeMinHeight()
            return
        }

        const response = await this.fetch()
        if (!response) {
            this.removeMinHeight()
            return
        }

        // Create HTML content for carousel
        const vehicles = response.vehicles
            .filter((vehicle) =>
                // eslint-disable-next-line no-null/no-null
                [null, 'SALE_PENDING'].includes(vehicle.websiteState)
            )
            .slice(0, VEHICLES_MAX_COUNT)
        const htmlContent = vehicles
            .map(
                (vehicle) => `
            <li data-carousel-item>
                <article data-aqa="clutch-vehicle-card" class="clutch-vehicle-card">
                    <a class="clutch-vehicle-card__content"
                    href="${this.getVehicleUrl(
                        vehicle
                    )}" target="_blank" rel="nofollow" "
                    ${this.getClickEventAttributes(
                        this.getVehicleUrl(vehicle)
                    )}" aria-label="Explore more details about this Clutch used Vehicle">
                        <div class="clutch-vehicle-card__img-wrapper">
                            <img class="clutch-vehicle-card__img"
                                width="100%"
                                height="100%"
                                src="${this.getCardPhotoUrl(vehicle)}"
                                alt="Vehicle outline drawing, no image available for now"
                                loading="lazy" />
                        </div>
                        <div class="clutch-vehicle-card__data">
                            <h3 class="clutch-vehicle-card__title">
                                <span class="clutch-vehicle-card__year">${
                                    vehicle.year
                                }</span>
                                <span class="clutch-vehicle-card__make">${
                                    vehicle.make.name
                                }</span>
                                <span class="clutch-vehicle-card__model">${
                                    vehicle.model.name
                                }</span>
                            </h3>
                            <p class="clutch-vehicle-card__description">
                                <span class="clutch-vehicle-card__trim">${
                                    vehicle.trim.name
                                }</span>
                                <span class="clutch-vehicle-card__drivetrain">${
                                    vehicle.drivetrain.name
                                }</span>
                                <span class="clutch-vehicle-card__mileage">&bull;&nbsp;${this.getMileageFormatted(
                                    vehicle
                                )} km</span>
                            </p>
                            <p class="clutch-vehicle-card__price">
                                ${this.getPriceHTML(vehicle)}
                            </p>
                        </div>
                    </a>
                </article>
            </li>
        `
            )
            .join('')
        const inventoryWrapper = document.querySelector<HTMLDivElement>(
            `.${className.inventoryWrapper}`
        )

        const seeMoreCard = this.wrapper.getAttribute('data-see-more-card')

        if (inventoryWrapper) {
            if (seeMoreCard == 'true') {
                inventoryWrapper.innerHTML = `
                    <div data-carousel-type="list"
                        data-carousel-icon-button
                        data-carousel-component
                        id="clutch-inventory-listing">
                        <h3 class="clutch-vehicle-inventory__title">${this.wrapper.getAttribute(
                            'data-title'
                        )}</h3>
                        <button data-carousel-prev type="button" tabindex="-1">
                            <span class="visually-hidden">Previous</span>
                        </button>
                        <ol class="list-unstyled" data-carousel-slide-list>
                            ${htmlContent}
                            <li data-carousel-item>
                                <article data-aqa="clutch-see-more-card" class="clutch-see-more-card">
                                    <div class="clutch-see-more-card__text">
                                        <p>See More</p>
                                        <p class="clutch-see-more-card__title">
                                            <span class="clutch-see-more-card__make">Used Vehicles</span>
                                        </p>
                                    </div>
                                    <a href="${this.getSeeMoreUrl(
                                        response.ctaURL
                                    )}" class="button button--primary-pill clutch-see-more-card__button"
                                    target="_blank"
                                    rel="nofollow" "
                                    ${this.getClickEventAttributes(
                                        this.getSeeMoreUrl(response.ctaURL)
                                    )}" aria-label="See more vehicles on Clutch.ca">View on Clutch.ca</a>
                                </article>
                            </li>
                        </ol>
                        <button data-carousel-next type="button" class="carousel__button--visible">
                            <span class="visually-hidden">Next</span>
                        </button>
                        <div class="clutch-vehicle-inventory__footer">
                            <span>Powered by</span>
                            <img width="60"
                                height="24"
                                src="${this.wrapper.getAttribute(
                                    'data-clutch-logo'
                                )}"
                                alt="Clutch logo"
                                loading="lazy"
                                class="clutch__logo" />
                        </div>
                    </div>
                `
            } else {
                inventoryWrapper.innerHTML = `
                    <div data-carousel-type="list"
                        data-carousel-icon-button
                        data-carousel-component
                        id="clutch-inventory-listing">
                        <div class="clutch-vehicle-inventory__header">
                            <h3 class="clutch-vehicle-inventory__title">${this.wrapper.getAttribute(
                                'data-title'
                            )}</h3>
                            <div class="clutch-see-more__button">
                            <a href="${this.getSeeMoreUrl(
                                response.ctaURL
                            )}" class="button button--tertiary-alt"
                                target="_blank"
                                rel="nofollow" "
                                ${this.getClickEventAttributes(
                                    this.getSeeMoreUrl(response.ctaURL)
                                )}" aria-label="See all vehicles on Clutch.ca">See All</a>
                            </div>
                        </div>
                        <button data-carousel-prev type="button" tabindex="-1">
                            <span class="visually-hidden">Previous</span>
                        </button>
                        <ol class="list-unstyled" data-carousel-slide-list>
                            ${htmlContent}
                        </ol>
                        <button data-carousel-next type="button" class="carousel__button--visible">
                            <span class="visually-hidden">Next</span>
                        </button>
                        <div class="clutch-vehicle-inventory__footer">
                            <span>Powered by</span>
                            <img width="60"
                                height="24"
                                src="${this.wrapper.getAttribute(
                                    'data-clutch-logo'
                                )}"
                                alt="Clutch logo"
                                loading="lazy"
                                class="clutch__logo" />
                        </div>
                    </div>
                `
            }
            new CarouselComponent(inventoryWrapper)
            this.removeMinHeight()
        }
        const navInventoryButton = document.querySelector<HTMLAnchorElement>(
            `.${className.inventoryNavButton}`
        )
        if (navInventoryButton) {
            navInventoryButton.style.display = 'block'
        }
    }

    private removeMinHeight() {
        const externalWrapper = document.querySelector<HTMLElement>(
            '.clutch-vehicle-inventory__external-wrapper'
        )
        if (externalWrapper) {
            externalWrapper.style.minHeight = '0'
        }
        document.documentElement.classList.remove('clutch-geo-cached')
    }

    private getLocationUID() {
        switch (
            this.geoContext &&
            'region' in this.geoContext &&
            this.geoContext.region.iso_code
        ) {
            case 'ON':
                return '214ce87c-6a70-4765-8bce-8b58a59ab558'
            default:
                return '568822fb-f5b8-4f19-81ef-2af098441c85'
        }
    }

    private getAPIUrl() {
        const params = new URLSearchParams({
            minResults: `${VEHICLES_MAX_COUNT}`
        })
        const base = `https://api.clutch.ca/v1/vehicles/locations/${this.getLocationUID()}`

        const settings = this.getWrapperSettings()
        Object.entries(settings).forEach(([key, values]) => {
            values.forEach((value) => {
                params.append(`${key}[]`, value)
            })
        })
        return `${base}?${params.toString()}`
    }

    private getWrapperSettings() {
        const fields = ['makes', 'models', 'bodyStyles', 'fuelTypes']
        return fields.reduce((acc, item) => {
            const val = (this.wrapper.getAttribute(`data-${item}`) || '')
                .split(',')
                .filter(Boolean)
            acc[item] = val
            return acc
        }, {} as Record<string, string[]>)
    }

    private async fetch() {
        const response = await fetch(this.getAPIUrl(), {
            headers: {
                'x-senwpbk': 'PGinInb3z22nfq1r6PFaYh21O3dmvU1A'
            }
        })
        if (!response.ok) {
            return
        }
        const result: ClutchResponse = await response.json()
        return result
    }

    private getMileageFormatted(vehicle: ClutchVehicle) {
        const mileageFormatted = vehicle.mileage
            .toLocaleString('en-CA', {
                style: 'currency',
                currency: 'CAD',
                maximumFractionDigits: 0
            })
            .slice(1)
        return mileageFormatted
    }

    private getVehicleUrl(vehicle: ClutchVehicle) {
        return `https://www.clutch.ca/vehicles/${vehicle.id}?utm_campaign=drivingca_vdp&utm_source=driving.ca&utm_medium=referral&utm_content=card`
    }

    private getSeeMoreUrl(ctaURL: string) {
        return `${ctaURL}?utm_campaign=drivingca_vdp&utm_source=driving.ca&utm_medium=referral&utm_content=seemore`
    }

    private getPreviousPrice(vehicle: ClutchVehicle) {
        if (vehicle.vehiclePrices.length) {
            return vehicle.vehiclePrices[vehicle.vehiclePrices.length - 1]
                .previousPrice
        }
        return ''
    }

    private getLatestPrice(vehicle: ClutchVehicle) {
        if (vehicle.vehiclePrices.length) {
            return vehicle.vehiclePrices[vehicle.vehiclePrices.length - 1].price
        }
        return ''
    }

    private getPriceHTML(vehicle: ClutchVehicle) {
        const previousPrice = this.getPreviousPrice(vehicle)
        const latestPriceStr = this.getLatestPrice(vehicle).toLocaleString(
            'en-CA',
            {
                style: 'currency',
                currency: 'CAD',
                maximumFractionDigits: 0
            }
        )

        if (previousPrice) {
            const previousPriceStr = previousPrice.toLocaleString('en-CA', {
                style: 'currency',
                currency: 'CAD',
                maximumFractionDigits: 0
            })
            return `<span class="clutch-vehicle-card__price-previous"
                aria-label="Previous price">${previousPriceStr}</span>
                <span class="clutch-vehicle-card__price-current" aria-label="Current price">${latestPriceStr}</span>`
        }
        return `<span class="clutch-vehicle-card__price-current" aria-label="Current price">${latestPriceStr}</span>`
    }

    private getCardPhotoUrl(vehicle: ClutchVehicle) {
        let photoUrl =
            'https://www.clutch.ca/images/coming-soon-vehicles/no-preview/coupe.svg'
        if (vehicle.cardPhotoUrl) {
            photoUrl = vehicle.cardPhotoUrl
        }
        return photoUrl
    }

    private getClickEventAttributes(url: string) {
        const dataEvtVal = {
            control_fields: {
                mparticle: {
                    keys: {
                        click_source_type: 'click_source_type',
                        anchor_text: 'anchor_text',
                        widget_title: 'widget_title',
                        target_url: 'target_url'
                    },
                    mp_event_type: 'Navigation',
                    extra_keys: [
                        'click_vertical_position_percentage',
                        'click_vertical_position_pixels'
                    ]
                }
            },
            click_source_type: 'widget-list',
            anchor_text: 'Clutch vehicle card',
            widget_title: 'Clutch - Inventory Listing',
            target_url: url
        }
        const clickEvent = `data-evt="click" data-evt-typ="click" data-evt-val='${JSON.stringify(
            dataEvtVal
        )}'`
        return clickEvent
    }
}
